import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useNavigate, useParams } from 'react-router';
import { useBackHref } from '@/hooks/useListLocation';
import { z } from 'zod';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';

const ProductDoc = graphql(/* GraphQL */ `
  query Product($id: ID!) {
    product(id: $id) {
      id
      name
      price
      tax
      isActive
      allowTable
      isBottomBar
      cashTrade
      consumptionName
      unitLabel
      unitAmount
    }
  }
`);

const CreateProductDoc = graphql(/* GraphQL */ `
  mutation CreateProduct($input: CreateProductInput!) {
    createProduct(input: $input) {
      id
    }
  }
`);

const UpdateProductDoc = graphql(/* GraphQL */ `
  mutation UpdateProduct($id: ID!, $input: UpdateProductInput!) {
    updateProduct(id: $id, input: $input) {
      id
      name
      price
      tax
      isActive
      consumptionName
      unitLabel
      unitAmount
    }
  }
`);

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  price: z.coerce.number().min(0, 'Price must be positive'),
  tax: z.coerce.number().min(0, 'Tax must be positive'),
  isActive: z.boolean(),
  allowTable: z.boolean(),
  isBottomBar: z.boolean(),
  cashTrade: z.boolean(),
  consumptionName: z.string().optional(),
  unitLabel: z.string().optional(),
  unitAmount: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? null : Number(v)),
    z.number().positive('Must be greater than 0').nullable().optional(),
  ),
});

export function ProductEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const backHref = useBackHref('/products');
  const isNew = !id || id === 'new';

  const { data, loading } = useQuery(ProductDoc, {
    variables: { id: id ?? '' },
    skip: isNew,
  });

  const [createProduct, { loading: creating }] = useMutation(CreateProductDoc);
  const [updateProduct, { loading: updating }] = useMutation(UpdateProductDoc);
  const saving = creating || updating;

  const product = data?.product;
  const [name, setName] = useState('');
  const [price, setPrice] = useState('0');
  const [tax, setTax] = useState('0');
  const [isActive, setIsActive] = useState(true);
  const [allowTable, setAllowTable] = useState(true);
  const [isBottomBar, setIsBottomBar] = useState(false);
  const [cashTrade, setCashTrade] = useState(false);
  const [consumptionName, setConsumptionName] = useState('');
  const [unitLabel, setUnitLabel] = useState('');
  const [unitAmount, setUnitAmount] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [seededFor, setSeededFor] = useState<string | null>(null);

  if (product && product.id !== seededFor) {
    setSeededFor(product.id);
    setName(product.name);
    setPrice(product.price.toFixed(2));
    setTax(product.tax.toFixed(2));
    setIsActive(product.isActive);
    setAllowTable(product.allowTable);
    setIsBottomBar(product.isBottomBar);
    setCashTrade(product.cashTrade);
    setConsumptionName(product.consumptionName ?? '');
    setUnitLabel(product.unitLabel ?? '');
    setUnitAmount(product.unitAmount != null ? String(product.unitAmount) : '');
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;
    setErrors({});
    const parsed = schema.safeParse({
      name,
      price,
      tax,
      isActive,
      allowTable,
      isBottomBar,
      cashTrade,
      consumptionName: consumptionName || undefined,
      unitLabel: unitLabel || undefined,
      unitAmount: unitAmount || undefined,
    });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string') fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    if (isNew) {
      await createProduct({ variables: { input: parsed.data } });
    } else if (id) {
      await updateProduct({ variables: { id, input: parsed.data } });
    }
    navigate(backHref);
  };

  if (!isNew && loading && !data) {
    return <p className="text-sm text-gray-500">Loading product…</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {isNew ? 'New product' : `Edit ${product?.name ?? ''}`}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Prices are stored in the scope's base currency. Tax is a percentage.
        </p>
      </div>

      <FormLayout
        title="Product"
        onSubmit={handleSubmit}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => navigate(backHref)}
            >
              Cancel
            </Button>
            <Button type="submit" id="admin.products.save" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create product' : 'Save product'}
            </Button>
          </div>
        }
      >
        <FormSection title="Basics">
          <FormField label="Name" required error={errors.name}>
            <Input
              id="admin.products.name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              invalid={Boolean(errors.name)}
              required
            />
          </FormField>
          <FormField label="Price" required error={errors.price}>
            <Input
              id="admin.products.price"
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              invalid={Boolean(errors.price)}
              required
            />
          </FormField>
          <FormField
            label="Tax (%)"
            required
            description="The percentage added on top of the net price."
            error={errors.tax}
          >
            <Input
              id="admin.products.tax"
              type="number"
              step="0.01"
              min="0"
              value={tax}
              onChange={(event) => setTax(event.target.value)}
              invalid={Boolean(errors.tax)}
              required
            />
          </FormField>
        </FormSection>

        <FormSection
          title="Consumption Report"
          description="Configure this product for the Consumption Report. All three fields must be set for the product to appear. Products with the same consumption name are merged into one report line."
        >
          <FormField
            label="Consumption name"
            description='Display label on the report and merge key. Products sharing the same name are summed (e.g. "Peroni" for all Peroni sizes).'
            error={errors.consumptionName}
          >
            <Input
              value={consumptionName}
              onChange={(event) => setConsumptionName(event.target.value)}
              placeholder={name || 'e.g. Peroni'}
            />
          </FormField>
          <FormField
            label="Unit label"
            description='The unit shown on the report (e.g. "CL", "can", "bottle").'
            error={errors.unitLabel}
          >
            <Input
              value={unitLabel}
              onChange={(event) => setUnitLabel(event.target.value)}
              placeholder="e.g. CL"
            />
          </FormField>
          <FormField
            label="Unit amount"
            description="How many units this product represents (e.g. 25 for a 25 CL bottle, 1 for a can)."
            error={errors.unitAmount}
          >
            <Input
              type="number"
              step="0.01"
              min="0"
              value={unitAmount}
              onChange={(event) => setUnitAmount(event.target.value)}
              placeholder="e.g. 25"
              invalid={Boolean(errors.unitAmount)}
            />
          </FormField>
        </FormSection>

        <FormSection
          title="Flags"
          description="Controls where and how this product shows up in the POS."
        >
          <FormField label="Active">
            <Switch
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
              label={isActive ? 'Available to sell' : 'Hidden'}
            />
          </FormField>
          <FormField label="Allow on tables">
            <Switch
              checked={allowTable}
              onChange={(event) => setAllowTable(event.target.checked)}
              label={allowTable ? 'Allowed' : 'Not allowed'}
            />
          </FormField>
          <FormField label="Show on bottom bar">
            <Switch
              checked={isBottomBar}
              onChange={(event) => setIsBottomBar(event.target.checked)}
              label={isBottomBar ? 'Pinned' : 'Regular'}
            />
          </FormField>
          <FormField label="Cash trade">
            <Switch
              checked={cashTrade}
              onChange={(event) => setCashTrade(event.target.checked)}
              label={cashTrade ? 'Cash only' : 'Any payment method'}
            />
          </FormField>
        </FormSection>
      </FormLayout>
    </div>
  );
}
